<template>
  <RouterLink
    :class="[
      'hero',
      { 'hero-active': active },
      { 'hero-light': hero.lightText },
    ]"
    :to="to"
    @click="onClick"
    ref="imgContainer"
  >
    <div class="hero-image">
      <div class="hero-caption hero-caption-sm">
        <BlockContent :blocks="caption" />
      </div>
      <img
        :srcset="hero.mobile.srcset"
        :src="hero.mobile.src"
        class="hero-img block md:hidden"
        :style="imgStyle"
      />
      <img
        :srcset="hero.desktop.srcset"
        :src="hero.desktop.src"
        class="hero-img hidden md:block"
        :style="imgStyle"
      />
    </div>
    <BlockContent
      class="hero-caption hero-caption-lg"
      :blocks="caption"
      :render-container-on-single-child="true"
    />
  </RouterLink>
</template>

<script lang="ts">
import { BlockText } from '@/sanity/blocks/types';
import { StoryPoster } from '@/types';
import {
  ComponentPublicInstance,
  defineComponent,
  PropType,
  reactive,
  ref,
  watch,
} from 'vue';
import { RouteLocationRaw } from 'vue-router';

export default defineComponent({
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    to: {
      type: Object as PropType<RouteLocationRaw>,
      required: true,
    },
    hero: {
      type: Object as PropType<StoryPoster>,
      required: true,
    },
    caption: {
      type: Object as PropType<BlockText>,
      default: '',
    },
  },
  emits: ['activate', 'animated'],
  setup(props, { emit }) {
    const imgContainer = ref<ComponentPublicInstance | null>(null);
    const imgStyle = reactive({ width: '100%', height: '100%' });

    const setDimensions = () => {
      if (props.active) {
        const isMobile = window.innerWidth < 800;
        const image = isMobile ? props.hero.mobile : props.hero.desktop;
        const newWidth = isMobile ? 100 / 2 : 100 / 2;
        const aspectRatio = image.width / image.height;
        const newHeight = newWidth / aspectRatio;
        imgStyle.width = `${newWidth.toString()}%`;
        imgStyle.height = `${newHeight.toString()}vw`;
      } else {
        imgStyle.width = '100%';
        imgStyle.height = '100%';
      }
    };

    setDimensions();

    const onClick = (e: Event) => {
      e.preventDefault();
      if (imgContainer.value) {
        emit('activate', imgContainer.value.$el);
      }
    };

    const onActivate = () => {
      setDimensions();
      setTimeout(() => {
        emit('animated');
      }, 700);
    };
    watch(() => props.active, onActivate);
    return { imgContainer, imgStyle, onClick };
  },
});
</script>
